import { nextTick } from "vue";
import { useRouter } from "vue-router";
import { DeliverableDisplay } from "@/common/constant";
import { routerPath } from "@/common/router";
import { SHOP_LIST_FILE_NAME } from "@/views/admin/ShopList/constants";
import type { ShopRegisterActionsDeps } from "../types";

/** 店舗登録画面のボタン操作・画面遷移を扱う composable。 */
export const useShopRegisterActions = (deps: ShopRegisterActionsDeps) => {
  const {
    shopCreateInput,
    displayConfirmDialog,
    displayCompletedDialog,
    overlay,
    validateOnMount,
    clickedBlockAddress,
    blockListFlg,
    dssSelectValues,
    dssFlg,
    updateMode,
    errdialog,
    errMessage,
    errorMessage,
    isDeliverableNumberDisplay,
    deliverableSelectValues,
    defaultDeliverable,
    holidaySelectValues,
    shippingLabelPrintSelectValues,
    shippingLabelPrintItems,
    deliveryOrderPrintSelectValues,
    deliveryOrderPrintItems,
    tmpMemberRegistrationSelectValues,
    tmpMemberRegistrationItems,
    chainSelectValues,
    chainViewFlag,
    dssListItems,
    dss_shop_name,
    createShopMutation,
    wordList,
    onBlurDssShopCd,
    getListDssConnectionSetting,
    checkShopName,
    createShop,
    allClear,
  } = deps;

  const router = useRouter();

  /**
   * 一覧へ戻る @Click
   */
  const onClickBack = async (): Promise<void> => {
    // 店舗登録確認ダイアログ 非表示
    displayConfirmDialog.value = false;
    await router.push({ name: SHOP_LIST_FILE_NAME, query: { searchMode: "true" } });
  };

  /**
   * 「チェーン」選択ボックス @change
   */
  const onChangeChain = async (
    selected?: { chain_code: string; name: string } | null,
  ): Promise<void> => {
    const chainCode =
      selected?.chain_code ||
      chainSelectValues.value?.chain_code ||
      deps.shopCreateInput.chain_code;
    //チェーンコードを設定
    shopCreateInput.chain_code = chainCode;
    //DSS接続先取得
    if (chainCode) {
      await getListDssConnectionSetting();
    }

    //店舗名が入力されていたら店舗名重複チェック
    if (
      shopCreateInput.name !== undefined &&
      shopCreateInput.name !== null &&
      shopCreateInput.name !== ""
    ) {
      checkShopName();
    }
  };

  /**
   * DSS接続先変更 @change
   */
  const onChangeDssConnectionId = async (
    connection_id: number,
    connection_check: boolean,
  ): Promise<void> => {
    if (connection_id !== 0) {
      dssFlg.value = true;
      if (connection_check) {
        onBlurDssShopCd();
      }
    } else {
      dssFlg.value = false;
    }
  };

  /**
   * 「登録手続きへ(確認)」ボタン @Click
   */
  const onClickOpenConfirmDialog = async (
    validate: () => Promise<{ valid: boolean }>,
  ): Promise<void> => {
    // バリデーションを実行
    const { valid } = await validate();
    if (!valid) {
      return;
    }
    // 丁目が選択されていない場合エラー
    if (!clickedBlockAddress.value && blockListFlg.value) {
      errorMessage.value = "丁目を選択してください";
      return;
    }
    onBlurDssShopCd();
    shopCreateInput.dss_connection_id = dssSelectValues.value!.connection_id;
    if (shopCreateInput.dss_connection_id == 0) {
      shopCreateInput.dss_shop_code = "";
    }
    // 店舗登録確認ダイアログ 表示
    displayConfirmDialog.value = true;
    validateOnMount.value = false;
  };

  /**
   * キャンセル @Click
   */
  const onClickConfirmCancel = async (): Promise<void> => {
    validateOnMount.value = true;
    // 店舗登録確認ダイアログ 非表示
    displayConfirmDialog.value = false;
  };

  /**
   * 店舗のみ登録する @Click
   */
  const onClickConfirmEntry = async (): Promise<void> => {
    overlay.value = true;
    try {
      // 休日設定
      await createHoliday();

      // 残数表示方法設定
      await createDeliverableDisplay();

      // 店舗作成
      await createShop();

      // 店舗登録確認ダイアログ 非表示
      displayConfirmDialog.value = false;

      // 店舗登録完了ダイアログ 表示
      displayCompletedDialog.value = true;
    } catch (err: unknown) {
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return;
    } finally {
      overlay.value = false;
    }
  };

  /**
   * 休日設定を作成
   */
  const createHoliday = async (): Promise<void> => {
    // 初期化する
    shopCreateInput.holiday_mon = 0;
    shopCreateInput.holiday_tue = 0;
    shopCreateInput.holiday_wed = 0;
    shopCreateInput.holiday_thu = 0;
    shopCreateInput.holiday_fri = 0;
    shopCreateInput.holiday_sat = 0;
    shopCreateInput.holiday_sun = 0;

    for (const holidaykey of holidaySelectValues.value) {
      switch (String(holidaykey.value)) {
        case "1":
          shopCreateInput.holiday_mon = 1;
          break;
        case "2":
          shopCreateInput.holiday_tue = 1;
          break;
        case "3":
          shopCreateInput.holiday_wed = 1;
          break;
        case "4":
          shopCreateInput.holiday_thu = 1;
          break;
        case "5":
          shopCreateInput.holiday_fri = 1;
          break;
        case "6":
          shopCreateInput.holiday_sat = 1;
          break;
        case "7":
          shopCreateInput.holiday_sun = 1;
          break;
      }
    }
  };

  /**
   * 配達残数の表示方法の設定
   */
  const createDeliverableDisplay = async (): Promise<void> => {
    // 数字の場合は設定しない
    if (isDeliverableNumberDisplay.value) {
      shopCreateInput.alert_lower_threshold = undefined;
      shopCreateInput.alert_upper_threshold = undefined;
    } else {
      // 下限はデフォルト値を設定する
      shopCreateInput.alert_lower_threshold =
        DeliverableDisplay.triangle_min_value;
    }
  };

  /**
   * 戻る @Click（完了画面）
   */
  const onClickCompletedGoStart = async (): Promise<void> => {
    //入力項目をクリア（フォームが非表示の間にデータをリセットする）
    await allClear();

    // validateOnMount を無効化してフォーム再マウント時のバリデーションを抑止
    validateOnMount.value = false;

    // 店舗登録完了ダイアログ 非表示（フォームがクリーンな状態で再マウントされる）
    displayCompletedDialog.value = false;

    await nextTick();
    // 「店舗登録」画面へ遷移
    await router.push({ path: routerPath.shopRegister });
  };

  return {
    onClickBack,
    onChangeChain,
    onChangeDssConnectionId,
    onClickOpenConfirmDialog,
    onClickConfirmCancel,
    onClickConfirmEntry,
    onClickCompletedGoStart,
    createHoliday,
    createDeliverableDisplay,
  };
};
